import { MouseEvent, KeyboardEvent, useRef, useEffect, ReactElement, useId, useState } from 'react'
import {
  GroupBase,
  OptionProps,
  components,
  MenuProps,
  ControlProps,
  SelectComponentsConfig,
  MultiValueGenericProps,
  SingleValueProps,
  InputProps,
  MenuListProps,
  AriaGuidanceProps,
  AriaOnFocusProps,
  ActionMeta,
  Props as ReactSelectBaseProps
} from 'react-select'
import { CheckIcon, ChevronRightIcon, SearchIcon } from '@chakra-ui/icons'
import colors from '~/theme/colors'
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Checkbox,
  Divider,
  Flex,
  HStack,
  Radio,
  ReactSelect,
  Stack,
  Text,
  Tooltip,
  VisuallyHidden,
  useFormControlContext
} from '~/design_system'
import { Msg, useMsg } from '~/common/localizationUtils'
import { childrenToString } from '~/common/childrenUtils'
import { frozenArr } from '~/common/arrayUtils2'
import usePrevious from '~/hooks/usePrevious'
import { assignRef } from '~/common/reactUtils'
import createDebugLogger from '~/common/logger'

const debug = createDebugLogger('TreeSelect')

type TreeSelectGroup = GroupBase<TreeSelectOption>

type TreeSelectValue<IsMulti extends boolean> = IsMulti extends true ? TreeSelectOption[] : TreeSelectOption

export type TreeSelectProps<IsMulti extends boolean = boolean> = {
  options: TreeSelectOption[]
  onChange: (value: TreeSelectValue<IsMulti>) => void
  onSearch?: (searchTerm: string) => void
  searchValue?: string
  onTraverseTree?: (newNode: TreeSelectPathSegment | null) => void // null means navigate to root
  isMulti?: IsMulti
  placeholder?: string | ReactElement<any>
  isDisabled?: boolean
  isLoading?: boolean
  recentlyUsed?: TreeSelectOption[]
  isClearable?: boolean
  id?: string
  name?: string
  value?: TreeSelectValue<IsMulti>
  menuPortalTarget?: HTMLElement | null
  'data-testid'?: string
  customReactSelectComponents?: Partial<SelectComponentsConfig<TreeSelectOption, IsMulti, TreeSelectGroup>>
  customFilter?: ((option: any, rawInput: string) => boolean) | undefined
  menuWidth?: 'content' | 'control'
  isBranchSelectable?: boolean
}

// ReactSelect uses forwardRef which erases generic type params in TypeScript.
// This typed alias preserves InternalTreeSelectOption/IsMulti so that `components`
// and `ariaLiveMessages` type-check without `as any`.
type TypedReactSelectProps<IsMulti extends boolean> = ReactSelectBaseProps<
  InternalTreeSelectOption,
  IsMulti,
  GroupBase<InternalTreeSelectOption>
> & {
  menuPortal?: boolean
  'data-testid'?: string
  onTraverseTree?: (newNode: TreeSelectPathSegment | null) => void
  hideRecentlyUsed?: boolean
  searchValue?: string
  isBranchSelectable?: boolean
}

const TypedReactSelect = ReactSelect as unknown as <IsMulti extends boolean = boolean>(
  props: TypedReactSelectProps<IsMulti>
) => ReactElement<any>

export type TreeSelectOption = {
  value: string | number
  label: string
  hasChildren: boolean
  isDisabled?: boolean
  path: TreeSelectPathSegment[]
  secondLine?: string
  thirdLine?: string
  code?: string
}

type InternalTreeSelectOption = TreeSelectOption & {
  isRecentlyUsed?: boolean
}

export type TreeSelectPathSegment = Omit<TreeSelectOption, 'path' | 'hasChildren'>
type TreeSelectOptionProps = OptionProps<InternalTreeSelectOption, boolean, GroupBase<InternalTreeSelectOption>>

const BranchOption = ({
  data,
  isFocused,
  isDisabled,
  innerRef,
  innerProps,
  selectProps,
  getValue
}: TreeSelectOptionProps) => {
  const msg = useMsg()
  const { label, secondLine, thirdLine } = data
  const hasSelectedDescendant = getValue().some(selected => selected.path.some(segment => segment.value === data.value))

  const handleClick = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (isDisabled) return
    ;(selectProps as any).onTraverseTree(data)
  }

  return (
    <Flex
      ref={innerRef}
      {...innerProps}
      data-value={data.value}
      className={`react-select__option react-select-option${isFocused ? ' react-select__option--is-focused' : ''}`}
      role="option"
      aria-disabled={isDisabled || undefined}
      alignItems="center"
      cursor={isDisabled ? 'default' : 'pointer'}
      opacity={isDisabled ? 0.4 : 1}
      bg={isFocused ? 'var(--vndly-color-button-background-secondary-hover)' : 'transparent'}
      transition="background-color 0.1s"
      onClick={handleClick}
      data-testid={`tree-select-branch-${label}`}>
      <Stack py={2} px={3} spacing={0} flex={1}>
        <Text textStyle="body" m={0} data-select-option-label>
          {label}
        </Text>
        {secondLine && (
          <Text m={0} textStyle="subtextSmall" data-select-option-sublabel>
            {secondLine}
          </Text>
        )}
        {thirdLine && (
          <Text m={0} textStyle="subtextSmall">
            {thirdLine}
          </Text>
        )}
      </Stack>
      {hasSelectedDescendant && (
        <CheckIcon
          color="var(--vndly-color-input-background-checked)"
          w={3}
          h={3}
          mr={2}
          data-testid={`tree-select-selected-descendant-${label}`}
        />
      )}
      <Box
        as="button"
        type="button"
        tabIndex={-1}
        disabled={isDisabled}
        aria-label={msg('tree_select.navigate_into_item', { item: label })}
        w="44px"
        display="flex"
        justifyContent="center">
        <ChevronRightIcon w="24px" h="24px" />
      </Box>
    </Flex>
  )
}

const CustomOption = (props: TreeSelectOptionProps) => {
  const msg = useMsg()
  const { data, isSelected, isFocused, isDisabled, innerRef, innerProps, selectProps, selectOption } = props
  const { hasChildren, label, isRecentlyUsed, secondLine, thirdLine } = data
  const isMultiSelect = selectProps.isMulti
  const formControl = useFormControlContext()
  const id = useId()
  const handleOnTraverseTree = (selectProps as any).onTraverseTree
  const options = selectProps.options as InternalTreeSelectOption[]
  const hideRecentlyUsed = (selectProps as any).hideRecentlyUsed || false
  const isBranchSelectable = (selectProps as any).isBranchSelectable ?? true
  const isSearching = ((selectProps as any).searchValue ?? '').trim() !== ''

  const sectionHeaderStyles = {
    py: 2,
    px: 3,
    borderBottom: '1px solid',
    borderColor: colors.gray[200],
    fontSize: 'sm',
    fontWeight: 'semibold',
    color: colors.gray[600],
    justify: 'space-between'
  }

  const handleSelectionClick = (e: MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()

    if (isMultiSelect) {
      // For multi-select, handle the selection manually using selectProps.onChange
      const currentValues: InternalTreeSelectOption[] = Array.isArray(selectProps.value) ? selectProps.value : []
      let newValues: InternalTreeSelectOption[]
      const action = isSelected ? 'deselect-option' : 'select-option'

      if (isSelected) {
        // Remove this option
        newValues = currentValues.filter((item: InternalTreeSelectOption) => item.value !== data.value)
      } else {
        // Add this option
        newValues = [...currentValues, data]
      }

      selectProps.onChange?.(newValues, { action, option: data })
    } else {
      // For single select, use react-select's selectOption method
      selectOption(data)
    }
  }

  const handleGoToChild = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    handleOnTraverseTree(data)
  }

  // Build CSS classes that react-select expects
  // We need this because we're implementing our own option component
  const optionClasses = [
    'react-select__option',
    'react-select-option',
    isSelected && 'react-select__option--is-selected',
    isFocused && 'react-select__option--is-focused'
  ]
    .filter(Boolean)
    .join(' ')

  const isFirstRecentlyUsed = data.value === options.find(opt => opt.isRecentlyUsed)?.value
  const isFirstOption = data.value === options.find(opt => !opt.isRecentlyUsed)?.value

  const showRecentlyUsed = isFirstRecentlyUsed && !hideRecentlyUsed && data.isRecentlyUsed
  const showCurrentLevel = isFirstOption && !hideRecentlyUsed && !data.isRecentlyUsed
  const showAll = !isBranchSelectable && isFirstOption && hideRecentlyUsed && data.path.length === 0 && !isSearching

  const sectionHeaders = (
    <>
      {showRecentlyUsed && (
        <div role="presentation">
          <HStack {...sectionHeaderStyles}>
            <Msg id="tree_select.recently_used" noProviderFoundMsg="Recently Used" />
          </HStack>
        </div>
      )}
      {showCurrentLevel && (
        <div role="presentation">
          <HStack {...sectionHeaderStyles}>
            <Msg id="tree_select.current_level" />
          </HStack>
        </div>
      )}
      {showAll && (
        <div role="presentation">
          <HStack {...sectionHeaderStyles}>
            <Msg id="common.ui.all" />
          </HStack>
        </div>
      )}
    </>
  )

  if (!isBranchSelectable && hasChildren && !isRecentlyUsed) {
    return (
      <>
        {sectionHeaders}
        <BranchOption {...props} />
      </>
    )
  }

  return (
    <>
      {sectionHeaders}
      <Flex
        ref={innerRef}
        {...innerProps}
        data-value={data.value}
        className={optionClasses}
        role="option"
        {...{ [isMultiSelect ? 'aria-checked' : 'aria-selected']: isSelected }}>
        <HStack
          cursor="pointer"
          flex={1}
          transition="background-color 0.1s"
          onClick={handleSelectionClick}
          sx={{
            // Only apply focus/hover style if NOT hovering over the "navigate into child" sibling button
            ':not(:has(+ [data-nav-into-child-btn]:hover))': {
              bg: isFocused ? 'var(--vndly-color-button-background-secondary-hover)' : 'transparent'
            }
          }}>
          {/* Radio button for single select, Checkbox for multi-select */}
          <Box py={2} px={3} cursor="pointer" display="flex" alignItems="center" justifyContent="center">
            {isMultiSelect ? (
              <Checkbox
                id={formControl?.id ? `${formControl.id}-option-${id}` : id}
                isChecked={isSelected}
                isDisabled={isDisabled}
                aria-disabled={isDisabled ? true : undefined}
                size="lg"
                colorScheme="blue"
                pointerEvents="none"
                data-testid={`tree-select-checkbox-${data.label}`}
              />
            ) : (
              <Radio
                id={formControl?.id ? `${formControl.id}-option-${id}` : id}
                isChecked={isSelected}
                isDisabled={isDisabled}
                aria-disabled={isDisabled ? true : undefined}
                size="lg"
                colorScheme="blue"
                pointerEvents="none"
                data-testid={`tree-select-radio-${data.label}`}
              />
            )}
          </Box>
          <Stack py={2} pr={2} spacing={0} flex={1}>
            <Text textStyle="body" m={0} data-select-option-label data-testid={`bu-name-${data.label}`}>
              {label}
            </Text>
            {secondLine && (
              <Text m={0} textStyle="subtextSmall" data-select-option-sublabel>
                {secondLine}
              </Text>
            )}
            {thirdLine && (
              <Text m={0} textStyle="subtextSmall">
                {thirdLine}
              </Text>
            )}
          </Stack>
        </HStack>
        {hasChildren && !isRecentlyUsed && (
          <Box
            data-nav-into-child-btn
            as="button"
            onClick={handleGoToChild}
            aria-label={msg('tree_select.navigate_into_item', { item: label })}
            w="44px"
            _hover={{ bg: 'var(--vndly-color-button-background-secondary-hover)' }}
            transition="background-color 0.1s"
            position="relative"
            data-testid={`tree-select-nav-${data.label}`}>
            <ChevronRightIcon w="24px" h="24px" />
          </Box>
        )}
      </Flex>
    </>
  )
}

// Custom Menu Component
const CustomMenu = (props: MenuProps<InternalTreeSelectOption, boolean, GroupBase<InternalTreeSelectOption>>) => {
  const msg = useMsg()
  const { children, ...menuProps } = props
  const options = props.options as InternalTreeSelectOption[]
  const breadcrumbs = options.find(opt => !opt.isRecentlyUsed)?.path || []
  const breadcrumbCount = breadcrumbs.length
  const searchValue = (props.selectProps as any).searchValue || ''
  const handleOnTraverseTree = (props.selectProps as any).onTraverseTree
  const showBreadcrumbs = breadcrumbCount > 0 && searchValue.trim() === ''

  return (
    <components.Menu
      {...menuProps}
      className={`${(props.selectProps as any).inputId || 'tree-select'}-tree-select-menu`}
      data-testid="tree-select-dropdown">
      {showBreadcrumbs && (
        <Box px={3} py={2} borderBottom="1px solid" borderColor={colors.gray[200]}>
          <Breadcrumb
            spacing={1}
            separator={<ChevronRightIcon color={colors.gray[400]} />}
            fontSize="sm"
            listProps={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              flexWrap: 'nowrap',
              overflow: 'hidden'
            }}>
            {(() => {
              const items = []
              const needsEllipsis = breadcrumbCount > 2
              const needsParent = breadcrumbCount > 1

              // Always show root
              items.push(
                <BreadcrumbItem key="root" flex="1" minW="0" maxW="fit-content" data-testid="tree-select-breadcrumb">
                  <BreadcrumbLink
                    as="button"
                    type="button"
                    onClick={() => handleOnTraverseTree(null)}
                    aria-label={msg('tree_select.navigate_to_root_level')}
                    textOverflow="ellipsis"
                    overflow="hidden"
                    whiteSpace="nowrap"
                    cursor="pointer"
                    color="var(--vndly-color-link)"
                    _hover={{ color: 'var(--vndly-color-link-hover)' }}
                    display="block">
                    <Msg id="common.ui.all" />
                  </BreadcrumbLink>
                </BreadcrumbItem>
              )

              if (needsEllipsis) {
                items.push(
                  <BreadcrumbItem key="ellipsis" flexShrink="0">
                    <Text m={0} color={colors.gray[400]} cursor="default">
                      ...
                    </Text>
                  </BreadcrumbItem>
                )
              }

              if (needsParent) {
                const parent = breadcrumbs[breadcrumbCount - 2]
                items.push(
                  <BreadcrumbItem key={parent.value} flex="1" minW="0" maxW="fit-content">
                    <BreadcrumbLink
                      as="button"
                      type="button"
                      onClick={() => handleOnTraverseTree(breadcrumbs.at(-2)!)}
                      aria-label={msg('tree_select.navigate_to_item', { item: parent?.label })}
                      data-testid={`tree-select-breadcrumb-${parent?.label}`}
                      textOverflow="ellipsis"
                      overflow="hidden"
                      whiteSpace="nowrap"
                      cursor="pointer"
                      color="var(--vndly-color-link)"
                      _hover={{ color: 'var(--vndly-color-link-hover)' }}
                      display="block">
                      {parent?.label}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                )
              }

              // Always add current (last) breadcrumb as non-clickable
              const current = breadcrumbs[breadcrumbCount - 1]
              items.push(
                <BreadcrumbItem key={current.value} isCurrentPage flex="1" minW="0" maxW="fit-content">
                  <Text
                    m={0}
                    pt="1px"
                    color={colors.gray[800]}
                    fontWeight="semibold"
                    cursor="default"
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis">
                    {current.label}
                  </Text>
                </BreadcrumbItem>
              )

              return items
            })()}
          </Breadcrumb>
        </Box>
      )}
      {children}
      <Divider />
      <Text
        textStyle="subtextSmall"
        mt={showBreadcrumbs ? 1 : 2}
        px={3}
        pb={2}
        width="min-content"
        minWidth="full"
        sx={{
          display: 'none',
          '[data-using-keyboard] &': { display: 'block' }
        }}>
        <Msg id="tree_select.keyboard_tip" />
      </Text>
    </components.Menu>
  )
}

const CustomMenuList = ({
  innerRef,
  children,
  ...props
}: MenuListProps<InternalTreeSelectOption, boolean, GroupBase<InternalTreeSelectOption>>) => {
  const options = props.options as InternalTreeSelectOption[]
  const breadcrumbs = options.find(opt => !opt.isRecentlyUsed)?.path || []
  const prevBreadcrumbs = usePrevious(breadcrumbs) || frozenArr

  const menuListRef = useRef<HTMLElement | null>(null)
  const scrollPositionsRef = useRef<Record<string, number>>({})
  useEffect(() => {
    const breadcrumbsHash = breadcrumbs.map(bc => bc.value).join('>')

    debug('CustomMenuList nav', { prevBreadcrumbs, breadcrumbs, scrollPositionsRef: scrollPositionsRef.current })
    // Navigating downward - scroll to top
    if (prevBreadcrumbs.length < breadcrumbs.length) {
      menuListRef.current?.scrollTo?.({ top: 0, behavior: 'instant' })
    }

    // Navigating upward - restore previous scroll position if available
    if (prevBreadcrumbs.length > breadcrumbs.length) {
      // Restoring previous scroll position if available
      const previousScrollTop = scrollPositionsRef.current[breadcrumbsHash] || 0
      menuListRef.current?.scrollTo?.({ top: previousScrollTop, behavior: 'instant' })
    }

    // Capture scroll positions per breadcrumb path as scrolling happens
    const captureScrollPosition = () => {
      scrollPositionsRef.current[breadcrumbsHash] = menuListRef.current?.scrollTop || 0
    }
    menuListRef.current?.addEventListener('scroll', captureScrollPosition)
    return () => {
      menuListRef.current?.removeEventListener('scroll', captureScrollPosition)
    }
  }, [breadcrumbs, prevBreadcrumbs])

  return (
    <components.MenuList
      innerRef={el => {
        assignRef(innerRef, el)
        menuListRef.current = el
      }}
      {...props}>
      {children}
    </components.MenuList>
  )
}

// Custom Control Component - used for single select scenario
const CustomControl = ({
  children,
  ...props
}: ControlProps<InternalTreeSelectOption, boolean, GroupBase<InternalTreeSelectOption>>) => {
  if (props.selectProps.isMulti) {
    return <components.Control {...props}>{children}</components.Control>
  }

  return (
    <Box position="relative">
      {props.selectProps.isSearchable && (
        <SearchIcon
          position="absolute"
          left="10px"
          top="50%"
          transform="translateY(-50%)"
          color={colors.gray[400]}
          w={4}
          h={4}
          zIndex={2}
          pointerEvents="none"
        />
      )}
      <components.Control {...props}>{children}</components.Control>
    </Box>
  )
}

// Custom input - used for multi-select search input
const CustomInput = ({
  selectProps,
  ...props
}: InputProps<InternalTreeSelectOption, boolean, GroupBase<InternalTreeSelectOption>>) => {
  if (!selectProps.isMulti) {
    return <components.Input data-react-select-input selectProps={selectProps} {...props} />
  }

  const hasValue = props.getValue().length > 0
  const placeholder = hasValue ? undefined : childrenToString(selectProps.placeholder)

  return (
    <>
      <Stack minWidth={0} flexDirection="row" alignItems="center" flexBasis="100%">
        <SearchIcon color="var(--vndly-color-container-subdued)" w={4} h={4} pointerEvents="none" />
        <components.Input data-react-select-input selectProps={selectProps} {...props} placeholder={placeholder} />
      </Stack>
    </>
  )
}

/**
 * Add tooltip support to multi-select options because they can be squashed in filter bars
 */
const CustomMultiValueLabel = ({
  children,
  ...props
}: MultiValueGenericProps<InternalTreeSelectOption, boolean, GroupBase<InternalTreeSelectOption>>) => {
  return (
    <components.MultiValueLabel {...props}>
      <Tooltip label={props.data.label}>{children}</Tooltip>
    </components.MultiValueLabel>
  )
}

/**
 * Add tooltip support to single select options in case they are squashed on responsive
 */
const CustomSingleValue = ({
  children,
  ...props
}: SingleValueProps<InternalTreeSelectOption, boolean, GroupBase<InternalTreeSelectOption>>) => {
  const data = props.data
  return (
    <components.SingleValue {...props}>
      <Box position="relative">
        <Tooltip position="relative" label={data.label}>
          {children}
        </Tooltip>
      </Box>
    </components.SingleValue>
  )
}

// react-select's built-in LiveRegion gates announcements on isFocused, which goes
// false on blur. In single-select mode blur fires synchronously with selection, so
// the onChange message is never read by screen readers.
//
// This hook owns all TreeSelect aria concerns:
//   • guidance  – what to say when menu / input / value chips receive focus
//   • onFocus   – level + navigation hints when an option is focused
//   • onChange   – announced via our own persistent live region (not react-select's)
//
// See: https://github.com/JedWatson/react-select/blob/052e864b/packages/react-select/src/accessibility/index.ts
const useTreeSelectAria = <IsMulti extends boolean>({
  breadcrumbs,
  options,
  onChange
}: {
  breadcrumbs: TreeSelectPathSegment[]
  options: TreeSelectOption[]
  onChange: (value: TreeSelectValue<IsMulti>) => void
}) => {
  const msg = useMsg()
  const [changeAnnouncement, setChangeAnnouncement] = useState('')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  // --- guidance: context-level instructions ---

  const getGuidanceMessage = ({ context }: AriaGuidanceProps) => {
    switch (context) {
      case 'menu':
        return msg('tree_select.guidance.menu')
      case 'value':
        return msg('tree_select.guidance.value')
      default:
        return ''
    }
  }

  // --- onFocus: option-level announcements with tree context ---

  const getFocusMessage = ({
    context,
    focused,
    label,
    isDisabled,
    isSelected
  }: AriaOnFocusProps<InternalTreeSelectOption, GroupBase<InternalTreeSelectOption>>) => {
    if (context !== 'menu') return label

    const level = breadcrumbs.length + 1
    const currentLevelName = breadcrumbs.at(-1)?.label || msg('common.ui.all')
    const parentLevelName = breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 2]?.label?.trim() : ''

    return [
      label,
      msg('tree_select.focus.level_announcement', { level, current_level_name: currentLevelName }),
      isSelected ? msg('tree_select.focus.selected') : '',
      isDisabled ? msg('tree_select.focus.unavailable') : '',
      focused.hasChildren && !focused.isRecentlyUsed ? msg('tree_select.focus.has_sublevels') : '',
      breadcrumbs.length > 0
        ? [
            parentLevelName
              ? msg('tree_select.focus.navigate_up', { parent_level_name: parentLevelName })
              : msg('tree_select.focus.navigate_up_generic'),
            msg('tree_select.focus.navigate_root')
          ].join(' ')
        : ''
    ]
      .filter(Boolean)
      .join(' ')
  }

  // --- onChange: build announcement string for a selection action ---

  const getChangeMessage = (action: string, label: string, isDisabled: boolean) => {
    if (action === 'clear') return msg('tree_select.change.cleared')
    if (action === 'select-option') {
      if (isDisabled) return msg('tree_select.change.unavailable', { item: label })
      return msg('tree_select.change.selected', { item: label })
    }
    if (action === 'deselect-option' || action === 'remove-value' || action === 'pop-value') {
      return msg('tree_select.change.removed', { item: label })
    }
    return ''
  }

  // --- handleChange: wraps consumer onChange to announce via persistent live region ---

  const handleChange = (
    newValue: TreeSelectValue<IsMulti> | null,
    actionMeta: ActionMeta<InternalTreeSelectOption>
  ) => {
    // In single-select, react-select doesn't pass `option` in actionMeta
    // (setValue is called without the 3rd arg). Fall back to newValue.
    const option =
      actionMeta.option ?? (newValue && !Array.isArray(newValue) ? (newValue as InternalTreeSelectOption) : null)
    const label = option?.label ?? ''
    const announcement = getChangeMessage(
      actionMeta.action,
      label,
      option ? options.some(o => o.value === option.value && o.isDisabled) : false
    )
    if (announcement) {
      if (timerRef.current) clearTimeout(timerRef.current)
      setChangeAnnouncement('')
      timerRef.current = setTimeout(() => setChangeAnnouncement(announcement), 150)
    }
    onChange(newValue as TreeSelectValue<IsMulti>)
  }

  // --- assembled return values ---

  const liveMessages = { guidance: getGuidanceMessage, onFocus: getFocusMessage }

  const liveRegion = (
    <VisuallyHidden aria-live="assertive" aria-atomic="true" role="status">
      {changeAnnouncement}
    </VisuallyHidden>
  )

  return { handleChange, liveMessages, liveRegion }
}

export const TreeSelect = <IsMulti extends boolean = boolean>({
  options: providedOptions,
  onChange,
  onSearch: providedOnSearch,
  searchValue: providedSearchValue = '',
  onTraverseTree: providedOnTraverseTree,
  isMulti = false as IsMulti,
  placeholder,
  isDisabled = false,
  isLoading = false,
  recentlyUsed = [],
  isClearable = true,
  value,
  id,
  name,
  customFilter = undefined,
  'data-testid': dataTestId,
  customReactSelectComponents,
  menuWidth = 'content',
  isBranchSelectable = true,
  ...props
}: TreeSelectProps<IsMulti>) => {
  const [internalPath, setInternalPath] = useState<TreeSelectPathSegment[]>([])
  const [internalSearchValue, setInternalSearchValue] = useState('')
  const isSearchInternal = !providedOnTraverseTree && !providedOnSearch
  const onSearch = isSearchInternal ? setInternalSearchValue : providedOnSearch
  const searchValue = isSearchInternal ? internalSearchValue : providedSearchValue
  const searchTerm = searchValue.trim().toLowerCase()

  let options = providedOptions
  if (isSearchInternal && searchTerm) {
    options = providedOptions.filter(
      opt => (isBranchSelectable || !opt.hasChildren) && opt.label.toLowerCase().includes(searchTerm)
    )
  } else if (!providedOnTraverseTree && !searchTerm) {
    options = providedOptions.filter(
      opt =>
        opt.path.length === internalPath.length &&
        opt.path.every((segment, index) => segment.value === internalPath[index].value)
    )
  }

  const onTraverseTree = (node: TreeSelectPathSegment | null) => {
    if (providedOnTraverseTree) {
      providedOnTraverseTree(node)
      return
    }
    onSearch?.('')
    if (!node) {
      setInternalPath([])
      return
    }
    const ancestorIndex = internalPath.findIndex(segment => segment.value === node.value)
    if (ancestorIndex >= 0) {
      setInternalPath(internalPath.slice(0, ancestorIndex + 1))
      return
    }
    const nodePath = providedOptions.find(opt => opt.value === node.value)?.path ?? internalPath
    setInternalPath([...nodePath, { value: node.value, label: node.label }])
  }

  const searchable = Boolean(onSearch) || searchValue.trim() !== ''
  const isAtRootLevel = options.some(opt => opt.path.length === 0)
  const breadcrumbs = options[0]?.path || frozenArr
  const hideRecentlyUsed = !recentlyUsed || recentlyUsed.length === 0 || !isAtRootLevel || searchValue?.trim() !== ''
  const testId = dataTestId || (name ? `${name}-tree-select` : undefined)

  // Prepare final options list with recently used section if applicable
  let allOptions: InternalTreeSelectOption[] = frozenArr as unknown as InternalTreeSelectOption[]
  if (!isLoading) {
    allOptions = hideRecentlyUsed
      ? [...options]
      : [
          ...recentlyUsed.map(option => ({
            ...option,
            isRecentlyUsed: true
          })),
          ...options
        ]
  }

  const _customComponents: Partial<
    SelectComponentsConfig<InternalTreeSelectOption, IsMulti, GroupBase<InternalTreeSelectOption>>
  > = {
    Option: CustomOption,
    Menu: CustomMenu,
    MultiValueLabel: CustomMultiValueLabel,
    SingleValue: CustomSingleValue,
    MenuList: CustomMenuList,
    Input: CustomInput,
    Control: CustomControl,
    ...(customReactSelectComponents as unknown as Partial<
      SelectComponentsConfig<InternalTreeSelectOption, IsMulti, GroupBase<InternalTreeSelectOption>>
    >)
  }

  const { handleChange, liveMessages, liveRegion } = useTreeSelectAria<IsMulti>({ breadcrumbs, options, onChange })

  return (
    <>
      {liveRegion}
      <TypedReactSelect<IsMulti>
        id={id}
        name={name}
        className="tree-select"
        options={allOptions}
        onChange={(newValue, actionMeta) => {
          const option = actionMeta.option ?? (Array.isArray(newValue) ? null : (newValue as InternalTreeSelectOption))
          if (!isBranchSelectable && actionMeta.action === 'select-option' && option?.hasChildren) {
            onTraverseTree(option)
            return
          }
          handleChange(newValue as TreeSelectValue<IsMulti>, actionMeta as ActionMeta<InternalTreeSelectOption>)
        }}
        isMulti={isMulti}
        placeholder={placeholder}
        value={value}
        isDisabled={isDisabled}
        isLoading={isLoading}
        isSearchable={searchable}
        isClearable={isClearable}
        closeMenuOnSelect={!isMulti}
        hideSelectedOptions={false}
        components={_customComponents}
        autoFocus={false}
        openMenuOnFocus={false}
        blurInputOnSelect={false}
        onTraverseTree={onTraverseTree}
        hideRecentlyUsed={hideRecentlyUsed}
        searchValue={searchValue}
        isBranchSelectable={isBranchSelectable}
        ariaLiveMessages={liveMessages}
        data-testid={testId}
        filterOption={customFilter}
        styles={{
          menu: (provided: any) =>
            menuWidth === 'control'
              ? provided
              : {
                  ...provided,
                  minWidth: 'min(80vw, 320px)',
                  width: 'max-content',
                  maxWidth: 'min(80vw, 600px)'
                },
          control: (provided: any) => ({
            ...provided,
            paddingLeft: !isMulti && searchable ? '24px' : provided.paddingLeft
          }),
          input: (provided: any) => {
            if (!isMulti) {
              return provided
            }
            return { display: 'flex', minWidth: '100px', width: '100%' }
          },
          placeholder: (provided: any) => ({
            ...provided,
            display: isMulti && searchable ? 'none' : provided.display, // searchable multi does placeholder in CustomInput, inline with search icon
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: 'var(--vndly-color-input-text-placeholder)'
          }),
          option: (provided: any, state: any) => {
            // The option container
            let backgroundColor = 'transparent'
            if (state.isFocused) {
              backgroundColor = colors.blue[50]
            } else if (state.isSelected) {
              backgroundColor = colors.blue[100]
            }
            return {
              ...provided,
              backgroundColor,
              color: colors.gray[800],
              cursor: 'pointer',
              ':hover': {
                backgroundColor: state.isFocused ? colors.blue[100] : colors.gray[50]
              }
            }
          }
        }}
        inputValue={searchValue}
        onInputChange={(value: string, _action: any) => onSearch?.(value)}
        onBlur={() => {
          if (searchable && searchValue) {
            onSearch?.('')
          }
        }}
        onKeyDown={(event: KeyboardEvent) => {
          const { key } = event

          if (key === 'ArrowLeft' && event.shiftKey && breadcrumbs.length > 0) {
            event.preventDefault()
            event.stopPropagation()
            onTraverseTree(null)
            return
          }

          if (key === 'ArrowRight' || key === 'ArrowLeft') {
            // Find the menuId using the aria-controls attribute on the control element
            const menuId = (event.target as HTMLElement).getAttribute('aria-controls')
            if (!menuId) return

            // Find the menu element by ID
            const menuElement = document.getElementById(menuId)
            if (!menuElement) return

            // Find the currently focused option using react-select's class names
            const focusedOption =
              menuElement.querySelector('.react-select__option--is-focused') ??
              menuElement.querySelector('[aria-selected="true"]')
            if (!focusedOption) return

            event.preventDefault()
            event.stopPropagation()
            const optionValue = focusedOption.getAttribute('data-value')
            const currentOption = allOptions.find(opt => String(opt.value) === optionValue)
            if (
              key === 'ArrowRight' &&
              currentOption &&
              currentOption.hasChildren &&
              (isBranchSelectable || !currentOption.isDisabled) &&
              focusedOption.getElementsByTagName('button').length
            ) {
              // Go to children
              onTraverseTree(currentOption)
            } else if (key === 'ArrowLeft' && breadcrumbs.length > 0) {
              // Go to parent
              const parentNode = breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 2] : null
              onTraverseTree(parentNode)
            }
          }
        }}
        {...props}
      />
    </>
  )
}
